/*
 * =============================================================================
 * FILE HEADER — packages/healing/src/embed.ts
 * =============================================================================
 *
 * CHANGE LOG
 * ----------
 * | # | Date & Time (IST)      | Who               | Why              | What Changed                |
 * |---|------------------------|-------------------|------------------|-----------------------------|
 * | 1 | 2026-06-17 (Phase 2)   | Claude Sonnet 4.6 | Healing core     | Created — ONNX embed wrapper|
 *
 * WHICH FILE CALLS / IMPORTS THIS FILE
 * --------------------------------------
 * - packages/healing/src/heal.ts   (defaultEmbed as fallback EmbedFn)
 * - packages/healing/src/index.ts  (re-exports initHealing, defaultEmbed, EmbedFn)
 *
 * FUNCTIONS / EXPORTS COVERED
 * ----------------------------
 * EmbedFn          — type alias: (text: string) => Promise<Float32Array>
 * initHealing(modelDir?) → Promise<void>
 *   Pre-warm the ONNX model. Call once at app/CLI startup.
 *   If not called, the first embed() call initializes lazily (adds ~1s cold start).
 * defaultEmbed(text) → Promise<Float32Array>
 *   Embed a single text string using the cached ONNX pipeline.
 *   Returns a 384-dim normalized Float32Array (all-MiniLM-L6-v2 output dim).
 *
 * CALLER → CALLEE MAP
 * --------------------
 * heal.ts → defaultEmbed → getExtractor → pipeline('feature-extraction', ...)
 *
 * IMPORTANT POINTS
 * -----------------
 * 1. @huggingface/transformers is loaded via dynamic import. This prevents the
 *    module from being eagerly evaluated in tests — test suites inject a mock
 *    EmbedFn and never trigger the real ONNX initialization.
 * 2. The model Xenova/all-MiniLM-L6-v2 is downloaded (~23MB) to the HuggingFace
 *    cache directory on first use:
 *      - Windows: %USERPROFILE%\.cache\huggingface\hub
 *      - macOS/Linux: ~/.cache/huggingface/hub
 *    Subsequent calls use the cached model; no internet needed after first run.
 * 3. onnxruntime-node is included as a transitive dependency via
 *    @huggingface/transformers. It provides native Node.js ONNX execution
 *    which is faster than WASM (~2-5x). Verified installed via pnpm.
 * 4. The pipeline is a module-level singleton. Once loaded it stays resident
 *    for the process lifetime. Re-calling initHealing() with a different
 *    modelDir has no effect once the singleton is set.
 * 5. Pooling strategy: 'mean' (average all token embeddings) + normalize: true
 *    (L2 normalize to unit vector). This is the standard configuration for
 *    sentence-similarity tasks with all-MiniLM-L6-v2.
 *
 * ISSUES FACED
 * ------------
 * - @huggingface/transformers 3.x uses onnxruntime-node for Node environment
 *   and onnxruntime-web (WASM) for browser. The dynamic import handles both.
 * - The pipeline return type is opaque; typed as unknown and cast when used.
 *
 * =============================================================================
 */
// ── Constants ─────────────────────────────────────────────────────────────────
const MODEL_ID = "Xenova/all-MiniLM-L6-v2";
const EMBED_DIM = 384; // output dimension for all-MiniLM-L6-v2
// ── Singleton pipeline ────────────────────────────────────────────────────────
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let _pipeline = null;
async function getExtractor(modelDir) {
    if (_pipeline)
        return _pipeline;
    // Dynamic import so tests never pay the ONNX initialization cost.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const hf = await import("@huggingface/transformers");
    if (modelDir) {
        hf.env.localModelPath = modelDir;
        hf.env.allowRemoteModels = false;
    }
    _pipeline = await hf.pipeline("feature-extraction", MODEL_ID, {
        dtype: "q8", // quantized int8 (~23MB vs ~90MB fp32)
    });
    return _pipeline;
}
// ── Public API ────────────────────────────────────────────────────────────────
/**
 * Pre-warm the ONNX model.
 *
 * Call once at startup (CLI entry point, Studio init) to avoid cold-start
 * latency on the first heal() call.
 *
 * @param modelDir  Optional local directory containing the ONNX model files.
 *                  When set, remote downloads are disabled. Used for air-gapped
 *                  deployments and CI environments with a bundled model.
 */
export async function initHealing(modelDir) {
    await getExtractor(modelDir);
}
/**
 * Embed a text string using the cached all-MiniLM-L6-v2 ONNX model.
 *
 * Returns a 384-dimensional Float32Array (mean-pooled, L2-normalized).
 * On the first call this downloads and loads the model (~1s on broadband).
 * Subsequent calls are fast (<100ms on a typical laptop CPU).
 *
 * For testing, inject a mock via the `embedFn` parameter of `heal()` instead
 * of calling this directly.
 */
export async function defaultEmbed(text) {
    if (!text.trim()) {
        // Empty text — return zero vector (no semantic signal)
        return new Float32Array(EMBED_DIM);
    }
    const extractor = await getExtractor();
    const output = await extractor(text, { pooling: "mean", normalize: true });
    // output.data is a Float32Array of length EMBED_DIM (after mean pooling)
    return output.data instanceof Float32Array
        ? output.data
        : new Float32Array(output.data);
}
//# sourceMappingURL=embed.js.map